/* ControlArticlesPage — §30-33 + Publicar Ahora (Manual Override) */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { controlApi } from "../lib/controlApi";
import { Loader2, Search, Zap, X, Play, AlertTriangle, CheckCircle2, Plus, Pencil } from "lucide-react";

const QUICK_FILTERS = [
  { key: "today", label: "Hoy" },
  { key: "urgent", label: "Urgentes" },
  { key: "rio-grande", label: "Río Grande" },
  { key: "ushuaia", label: "Ushuaia" },
  { key: "tolhuin", label: "Tolhuin" },
  { key: "errors", label: "Errores" },
];

function chipCls(status) {
  if (status === "published" || status === "test_mode") return "bg-emerald-100 text-emerald-700";
  if (status === "queued" || status === "publishing" || status === "retrying") return "bg-amber-100 text-amber-700";
  if (status === "failed" || status === "disconnected" || status === "cancelled") return "bg-red-100 text-red-700";
  return "bg-slate-100 text-slate-500";
}

function scoreCls(v) {
  if (typeof v !== "number") return "bg-slate-100 text-slate-500";
  if (v >= 80) return "bg-emerald-100 text-emerald-700";
  if (v >= 50) return "bg-blue-100 text-blue-700";
  return "bg-slate-100 text-slate-500";
}

function statusChip(st) {
  const map = {
    selected: "bg-emerald-100 text-emerald-700",
    scored: "bg-amber-100 text-amber-700",
    detected: "bg-blue-100 text-blue-700",
    queued: "bg-amber-100 text-amber-700",
    discarded: "bg-slate-200 text-slate-600",
    unresolved: "bg-red-100 text-red-700",
    publication_error: "bg-red-100 text-red-700",
  };
  return map[st] || "bg-slate-100 text-slate-500";
}

export default function ControlArticlesPage() {
  const [tab, setTab] = useState("candidates"); // "candidates" | "published"
  const nav = useNavigate();

  return (
    <div data-testid="spc-articles" className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-textgray">Noticias</div>
          <div className="font-serif text-2xl font-bold text-blueblack">Motor editorial</div>
        </div>
        <button
          type="button"
          onClick={() => nav("/control/editor/new")}
          className="min-h-[44px] px-4 bg-blueblack text-white text-[11px] uppercase tracking-widest font-bold flex items-center gap-1.5"
          data-testid="spc-new-article-btn"
        >
          <Plus className="w-4 h-4" /> Nueva
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-softblue" role="tablist">
        <button
          data-testid="spc-tab-candidates"
          role="tab"
          aria-selected={tab === "candidates"}
          onClick={() => setTab("candidates")}
          className={`flex-1 h-11 text-xs font-bold uppercase tracking-widest ${
            tab === "candidates" ? "text-blueblack border-b-2 border-blueblack" : "text-textgray"
          }`}>
          Candidatas
        </button>
        <button
          data-testid="spc-tab-published"
          role="tab"
          aria-selected={tab === "published"}
          onClick={() => setTab("published")}
          className={`flex-1 h-11 text-xs font-bold uppercase tracking-widest ${
            tab === "published" ? "text-blueblack border-b-2 border-blueblack" : "text-textgray"
          }`}>
          Publicadas
        </button>
      </div>

      {tab === "candidates" ? <CandidatesList /> : <PublishedList />}
    </div>
  );
}

/* ============================================================
 *  CANDIDATES tab — Publicar Ahora
 * ============================================================ */
function CandidatesList() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [feedback, setFeedback] = useState(null); // {kind:"ok"|"err", message}

  const load = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const params = { page: reset ? 1 : page, page_size: 20, sort: "detected_at:desc" };
      if (q.trim()) params.q = q.trim();
      const r = await controlApi.candidates(params);
      const list = r.items || [];
      setItems(reset ? list : [...items, ...list]);
      setTotal(r.total || 0);
      if (reset) setPage(1);
    } catch (_e) { /* silent */ } finally { setLoading(false); }
  }, [q, page, items]);

  useEffect(() => { load(true); /* mount */ }, []);

  const onSearch = (e) => { e.preventDefault(); load(true); };

  const handlePublish = async (cand) => {
    const title = cand.generated_title || cand.original_title || cand.id;
    if (!window.confirm(`¿Publicar AHORA "${title}"?\n\nSe saltará el score automático pero seguirán los validadores técnicos.`)) return;
    setPublishingId(cand.id);
    setFeedback(null);
    try {
      const key = `pn-${cand.id}-${Date.now()}`;
      const res = await controlApi.publishCandidateNow(cand.id, { override_quota: true }, key);
      if (res?.ok) {
        setFeedback({ kind: "ok", message: `Publicada: ${res.title || res.slug}` });
        // Remove from candidates list (it's now published)
        setItems((prev) => prev.filter((c) => c.id !== cand.id));
        setTotal((t) => Math.max(0, t - 1));
      } else {
        setFeedback({ kind: "err", message: res?.message || "No se pudo publicar" });
      }
    } catch (e) {
      const err = e?.response?.data?.detail?.error;
      const msg = err?.message || "Error publicando la candidata";
      setFeedback({ kind: "err", message: msg });
    } finally { setPublishingId(null); }
  };

  return (
    <div className="space-y-3" data-testid="spc-candidates-list">
      <form onSubmit={onSearch} className="flex items-center gap-2 border border-softblue bg-white px-3 h-10">
        <Search className="w-4 h-4 text-textgray shrink-0" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="spc-candidates-search"
          placeholder="Buscar candidata…"
          className="flex-1 bg-transparent outline-none text-sm" />
        {q && <button type="button" onClick={() => { setQ(""); load(true); }} aria-label="Limpiar"><X className="w-4 h-4 text-textgray" /></button>}
      </form>

      {feedback && (
        <div
          data-testid={feedback.kind === "ok" ? "spc-publish-ok" : "spc-publish-err"}
          className={`flex items-start gap-2 p-3 border text-xs ${
            feedback.kind === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"
          }`}>
          {feedback.kind === "ok" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
          <div className="flex-1">{feedback.message}</div>
          <button onClick={() => setFeedback(null)} aria-label="Cerrar"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="p-6 text-center text-textgray text-xs"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-textgray text-xs">Sin candidatas.</div>
      ) : (
        <ul className="border border-softblue bg-white divide-y divide-softblue" data-testid="spc-candidates-ul">
          {items.map((c) => {
            const title = c.generated_title || c.original_title || "(sin título)";
            const img = c.generated_image_url || c.original_image_url;
            return (
              <li key={c.id} data-testid={`spc-candidate-${c.id}`} className="flex items-start gap-3 p-3">
                {img ? (
                  <img src={img} alt="" loading="lazy" className="w-16 h-16 object-cover bg-slate-100 shrink-0" />
                ) : (
                  <div className="w-16 h-16 bg-slate-100 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] text-textgray uppercase tracking-widest flex items-center gap-2">
                    <span>{c.detected_at ? new Date(c.detected_at).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                    {c.source_name && <span>· {c.source_name}</span>}
                    {c.location_id && <span>· {c.location_id}</span>}
                  </div>
                  <div className="font-serif text-sm font-bold text-blueblack line-clamp-2">
                    {c.potential_urgent && <Zap className="inline w-3.5 h-3.5 text-red-500 mr-0.5" />}
                    {title}
                  </div>
                  <div className="flex gap-1 mt-1 items-center flex-wrap">
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold ${scoreCls(c.editorial_score)}`}>
                      SCORE {c.editorial_score ?? "—"}
                    </span>
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${statusChip(c.editorial_status)}`}>
                      {c.editorial_status || "—"}
                    </span>
                    {c.publication_error_reason && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-700">
                        ⚠ {c.publication_error_reason}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <button
                      onClick={() => handlePublish(c)}
                      disabled={publishingId === c.id}
                      data-testid={`spc-publish-now-${c.id}`}
                      className="inline-flex items-center gap-1.5 px-3 min-h-[44px] bg-emerald-600 text-white text-[11px] font-bold uppercase tracking-widest disabled:opacity-60 hover:bg-emerald-700">
                      {publishingId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                      Publicar ahora
                    </button>
                    <Link
                      to={`/control/editor/candidate/${c.id}`}
                      data-testid={`spc-edit-candidate-${c.id}`}
                      className="inline-flex items-center gap-1.5 px-3 min-h-[44px] border border-softblue bg-white text-blueblack text-[11px] font-bold uppercase tracking-widest hover:bg-lightgray">
                      <Pencil className="w-3.5 h-3.5" />
                      Editar
                    </Link>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {items.length < total && (
        <button data-testid="spc-candidates-more" onClick={() => { setPage((p) => p + 1); load(false); }}
          className="w-full h-10 border border-softblue bg-white text-sm text-blueblack">
          Cargar más ({items.length} / {total})
        </button>
      )}
    </div>
  );
}

/* ============================================================
 *  PUBLISHED tab — same behaviour as before
 * ============================================================ */
function PublishedList() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(null);

  const load = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const params = { page: reset ? 1 : page, page_size: 20, sort: "published_at:desc" };
      if (q.trim()) params.q = q.trim();
      if (filter === "today") {
        const now = new Date();
        params.date_from = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      } else if (filter === "urgent") {
        params.urgent = true;
      } else if (["rio-grande", "ushuaia", "tolhuin"].includes(filter)) {
        params.location = filter;
      }
      const r = await controlApi.articles(params);
      let ls = r.items || [];
      if (filter === "errors") {
        ls = ls.filter((a) => ["failed", "disconnected"].includes(a.facebook_status) ||
                              ["failed", "disconnected"].includes(a.instagram_status));
      }
      setItems(reset ? ls : [...items, ...ls]);
      setTotal(r.total || 0);
      if (reset) setPage(1);
    } catch (_e) { /* noop */ } finally { setLoading(false); }
  }, [q, page, filter, items]);

  useEffect(() => { load(true); }, [filter]);

  const onSearch = (e) => { e.preventDefault(); load(true); };

  return (
    <div className="space-y-3" data-testid="spc-published-list">
      <form onSubmit={onSearch} className="flex items-center gap-2 border border-softblue bg-white px-3 h-10">
        <Search className="w-4 h-4 text-textgray shrink-0" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="spc-articles-search"
          placeholder="Buscar por título…"
          className="flex-1 bg-transparent outline-none text-sm" />
        {q && <button type="button" onClick={() => { setQ(""); load(true); }} aria-label="Limpiar"><X className="w-4 h-4 text-textgray" /></button>}
      </form>
      <div className="flex gap-1 overflow-x-auto -mx-1 px-1 no-scrollbar">
        <button onClick={() => setFilter(null)} data-testid="filter-all"
          className={`px-3 h-7 text-[11px] uppercase tracking-widest font-bold border shrink-0 ${!filter ? "bg-blueblack text-white border-blueblack" : "bg-white text-blueblack border-softblue"}`}>
          Todas
        </button>
        {QUICK_FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(filter === f.key ? null : f.key)}
            data-testid={`filter-${f.key}`}
            className={`px-3 h-7 text-[11px] uppercase tracking-widest font-bold border shrink-0 ${filter === f.key ? "bg-blueblack text-white border-blueblack" : "bg-white text-blueblack border-softblue"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading && items.length === 0 ? (
        <div className="p-6 text-center text-textgray text-xs"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-textgray text-xs">Sin resultados.</div>
      ) : (
        <ul className="border border-softblue bg-white divide-y divide-softblue">
          {items.map((art) => (
            <li key={art.id}>
              <Link to={`/control/articles/${art.id}`} className="flex items-start gap-3 p-3">
                {art.image_url ? (
                  <img src={art.image_url} alt="" loading="lazy" className="w-16 h-16 object-cover bg-slate-100 shrink-0" />
                ) : (
                  <div className="w-16 h-16 bg-slate-100 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] text-textgray uppercase tracking-widest flex items-center gap-2">
                    <span>{art.published_at ? new Date(art.published_at).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                    {art.main_category_name && <span>· {art.main_category_name}</span>}
                    {art.location_id && <span>· {art.location_id}</span>}
                  </div>
                  <div className="font-serif text-sm font-bold text-blueblack line-clamp-2">
                    {art.urgent && <Zap className="inline w-3.5 h-3.5 text-red-500 mr-0.5" />}
                    {art.title}
                  </div>
                  <div className="flex gap-1 mt-1 items-center flex-wrap">
                    {typeof art.editorial_score === "number" && (
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold ${scoreCls(art.editorial_score)}`}>
                        {art.editorial_score}
                      </span>
                    )}
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold ${chipCls(art.portal_status)}`}>WEB {art.portal_status === "published" ? "✓" : "—"}</span>
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold ${chipCls(art.facebook_status)}`}>FB {art.facebook_status === "published" ? "✓" : art.facebook_status === "failed" || art.facebook_status === "disconnected" ? "⚠" : "—"}</span>
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold ${chipCls(art.instagram_status)}`}>IG {art.instagram_status === "published" ? "✓" : art.instagram_status === "failed" || art.instagram_status === "disconnected" ? "⚠" : "—"}</span>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {items.length < total && (
        <button data-testid="spc-articles-more" onClick={() => { setPage((p) => p + 1); load(false); }}
          className="w-full h-10 border border-softblue bg-white text-sm text-blueblack">
          Cargar más ({items.length} / {total})
        </button>
      )}
    </div>
  );
}
